/**
 * Google Analytics and Google Ads conversion tracking helper
 * Direct integration with gtag.js for lightweight tracking
 */

import app from '../data/app.svelte.js';

let config = {
    google_analytics: null,
    google_ads: null
};

/**
 * Decode the GA4 cross-domain _gl linker parameter into a key/value map.
 * The _gl format is: 1*<fingerprint>*<key>*<base64 value>*<key>*<base64 value>*...
 * where keys are cookie names (_ga, _ga_XXXXXXXXXX, _gcl_aw, ...) and values are
 * base64-encoded with '.' replacing '=' padding (and URL-safe '-'/'_' variants).
 * The fingerprint is a browser+time hash only gtag can verify — we treat the
 * values as untrusted input and validate them before use.
 */
function decodeGl(gl) {
    const values = {};

    try {
        const parts = gl.split('*');

        for (let i = 2; i + 1 < parts.length; i += 2) {
            values[parts[i]] = atob(parts[i + 1].replace(/\./g, '=').replace(/-/g, '+').replace(/_/g, '/'));
        }
    } catch {
        // Malformed linker parameter — behave as if it wasn't there
        return {};
    }

    return values;
}

/**
 * Restore Google Ads click cookies (gclid/wbraid carried in the _gl linker) so
 * conversions fired on this domain still connect to the ad click that happened
 * on the salon's own site. Values are only accepted in the exact GCL cookie
 * format, since they come from the (spoofable) URL.
 */
function restoreAdsClickCookies(linker) {
    for (const name of ['_gcl_aw', '_gcl_dc', '_gcl_gb']) {
        if (linker[name] && /^GCL\.\d+\.[A-Za-z0-9_.-]+$/.test(linker[name])) {
            document.cookie = `${name}=${linker[name]}; max-age=7776000; path=/; SameSite=Lax`;
        }
    }
}

/**
 * Google appends the ad click ID to the landing page URL as ?gclid=. A salon
 * that passes it on to the widget link (instead of, or besides, the _gl
 * linker) gets the same attribution: the ID is stored the way gtag would
 * have stored it on the landing page. gtag itself never sees the entry URL,
 * since it is replaced by the session URL before consent is given and the
 * tag loads. Same format check as above: the value comes from the URL.
 */
function restoreAdsClickId(gclid) {
    if (gclid && /^[A-Za-z0-9_-]{1,200}$/.test(gclid)) {
        document.cookie = `_gcl_aw=GCL.${Math.floor(Date.now() / 1000)}.${gclid}; max-age=7776000; path=/; SameSite=Lax`;
    }
}

/**
 * Initialize tracking based on app settings
 * @param {Object} params - The entry URL's query parameters (session.params):
 *        _gl carries the Google linker, gclid a forwarded ad click ID
 * @returns {Object} - Tracking configuration
 */
export function initializeTracking(params = {}) {
    const linker = params._gl ? decodeGl(params._gl) : {};

    /* Set up configuration based on settings */

    if (app.settings.widget.analytics.enabled &&
        app.settings.widget.analytics.type === 'google-analytics' &&
        app.settings.widget.analytics.account) {
        config.google_analytics = { ...app.settings.widget.analytics };

        // Continue the visitor's GA4 client from the referring site, so the
        // booking is attributed to the original channel (ads, organic) instead
        // of a self-referral from the salon's own website
        if (/^\d+\.\d+$/.test(linker._ga || '')) {
            config.google_analytics.client_id = linker._ga;
        }
    }

    if (app.settings.widget.ads.enabled &&
        app.settings.widget.ads.type === 'google-ads' &&
        app.settings.widget.ads.account &&
        app.settings.widget.ads.label) {
        config.google_ads = { ...app.settings.widget.ads };
    }

    /* Initialize gtag.js if needed */

    if (config.google_analytics || config.google_ads) {
        if (!window.gtag) {
            restoreAdsClickCookies(linker);
            restoreAdsClickId(params.gclid);

            window.dataLayer = window.dataLayer || [];
            window.gtag = function() {
                window.dataLayer.push(arguments);
            };

            window.gtag('js', new Date());

            // Determine which ID to use for loading gtag.js script
            const tagManagerId = config.google_analytics?.account || config.google_ads?.account;

            if (config.google_analytics) {
                if (config.google_analytics.client_id) {
                    window.gtag('config', config.google_analytics.account, {
                        client_id: config.google_analytics.client_id,
                        // The visitor arrived via a cross-domain link from the
                        // salon's site; without this the session source becomes
                        // "salon site / referral" and steals the attribution
                        ignore_referrer: true
                    });
                } else {
                    window.gtag('config', config.google_analytics.account);
                }
            }

            if (config.google_ads) {
                window.gtag('config', config.google_ads.account);
            }

            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${tagManagerId}`;

            const firstScript = document.getElementsByTagName('script')[0];
            firstScript.parentNode.insertBefore(script, firstScript);
        }
    }

    return config;
}

/**
 * Track page view
 * @param {string} path - Page path (e.g., '/welcome')
 * @param {string} title - Page title (optional)
 * @param {Object} additionalData - Additional tracking data
 */
export function trackPageView(path, title = '', additionalData = {}) {
    if (!config.google_analytics || !window.gtag) {
        return;
    }

    const eventData = {
        page_path: path,
        ...additionalData
    };

    if (title) {
        eventData.page_title = title;
    }

    window.gtag('event', 'page_view', eventData);
}

/**
 * Track Google Ads conversion
 * @param {Object} conversionData - Conversion data (value, currency, transaction_id, etc.)
 */
export function trackConversion(conversionData = {}) {
    if (!config.google_ads || !window.gtag) {
        return;
    }

    window.gtag('event', 'conversion', {
        send_to: `${config.google_ads.account}/${config.google_ads.label}`,
        ...conversionData
    });
}


/**
 * Track booking funnel step
 * @param {string} step - Step name (e.g., 'treatment', 'employee', 'date', 'time', 'customer')
 * @param {Object} stepData - Additional step data
 */
export function trackStep(step) {
    if (!config.google_analytics || !window.gtag) {
        return;
    }

    window.gtag('event', step);
}
